import React, { useState } from "react";
import { Box, Button } from "@material-ui/core";
import { useStyles } from "./styles";
import CalendarIcon from "src/assets/icons/web/sidebar/calendar.svg";
import { CalendarFilters } from "src/web/components/CalendarManagement/CalendarFilters";
import { PageCardsLayout } from "src/web/layouts/PageCardsLayout";
import { InnerPageHeader } from "src/web/components/InnerPagesHeader";
import { useDispatch, useSelector } from "react-redux";
import { auth } from "src/redux/Authentication/sliceAuthentication";
import {
  allowBtns,
  calendarDates,
  calendarOptions,
  calendarWeek,
  normalisedData,
  setDetailsData,
  tripData,
} from "src/redux/Calendar/sliceCalendar";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_CALENDAR, UPDATE_CALENDAR } from "src/api/calendar";
import { setNotification } from "src/redux/Notifications/sliceNotifications";
import { convertDateRangesForQuery } from "src/helpers/calendar/calendarRequests";
import { InsertInvitation } from "@material-ui/icons";
import { CalendarBulkDialog } from "src/web/components/CalendarManagement/CalendarFilters/CalendarBulkDialog";
import { CalendarDetailsDialog } from "src/web/components/CalendarManagement/CalendarFilters/CalendarDetailsDialog";

const CalendarManagement = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useSelector(auth);
  const { detailsBtn } = useSelector(allowBtns);
  const { rate_id, trip_id, type, option_type, ticket_type, schedulesIds } =
    useSelector(calendarOptions);
  const dates = useSelector(calendarDates);
  const week = useSelector(calendarWeek);
  const detailedData = useSelector(normalisedData);
  const data = useSelector(tripData);
  const partner_id = +user["custom:partner_id"];
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkState, setBulkState] = useState<any>();

  const [getCalendar, { loading }] = useLazyQuery(GET_CALENDAR, {
    onCompleted: ({ v1_activities_getCalendar }) => {
      const calendarData = JSON.parse(v1_activities_getCalendar.data || "{}");

      if (!v1_activities_getCalendar.success) {
        return dispatch(
          setNotification({
            type: "error",
            message: v1_activities_getCalendar.msg?.join(",") || "",
          })
        );
      }
      dispatch(setDetailsData(calendarData));
    },
    onError: (error) => {
      dispatch(
        setNotification({
          message: error.message,
          type: "error",
        })
      );
    },
  });

  const [updateCalendar, { loading: updateLoading }] = useMutation(
    UPDATE_CALENDAR,
    {
      onCompleted: (data) => {
        if (JSON.parse(data.v1_activities_updateCalendar)?.success) {
          onCloseDetailsDialog();
          onCloseBulkDialog();
          dispatch(
            setNotification({
              message: "data update successfully",
              type: "success",
            })
          );
        }
      },
      onError: (error) => {
        dispatch(
          setNotification({
            message: error.message,
            type: "error",
          })
        );
      },
    }
  );

  const onCloseDetailsDialog = () => {
    dispatch(setDetailsData(undefined));
    setOpen(false);
  };

  const onCloseBulkDialog = () => {
    setBulkState(undefined);
    setBulkOpen(false);
  };

  const fetchCalendarData = async () => {
    setOpen(true);
    await getCalendar({
      variables: {
        prm: {
          user_id: +partner_id,
          partner_id: +partner_id,
          trip_id,
          rate_id: rate_id || 0,
          date_ranges: convertDateRangesForQuery(dates),
          days: week?.join(",") || "",
          type,
          schedules_id: schedulesIds?.join(",") || "",
          ...(type === "ticket" && { ticket_id: ticket_type }),
          ...(type === "option" && { option_id: option_type }),
        },
      },
    });
  };

  const applyChanges = () => {
    updateCalendar({
      variables: {
        prm: {
          user_id: +partner_id,
          trip_id,
          rate_id: rate_id || 0,
          partner_id: partner_id || 0,
          type: type,
          calendar_days: JSON.stringify(detailedData),
          ...(type === "ticket" && { ticket_id: ticket_type }),
          ...(type === "option" && { option_id: option_type }),
          ...(type === "schedule" && {
            schedule_id: Object.keys(detailedData)[0],
          }),
        },
      },
    });
  };

  const applyBatchChanges = () => {
    updateCalendar({
      variables: {
        prm: {
          user_id: +partner_id,
          trip_id,
          rate_id: rate_id || 0,
          partner_id: partner_id || 0,
          type: type,
          batch_data: JSON.stringify(bulkState),
          date_from: dates[0].date_from,
          date_to: dates[0].date_to,
          schedules_ids: schedulesIds,
          ...(type === "ticket" && { ticket_id: ticket_type }),
          ...(type === "option" && { option_id: option_type }),
          ...(type === "schedule" && {
            schedule_id: Object.keys(detailedData)[0],
          }),
        },
      },
    });
  };

  const openBulkDialog = () => {
    if (type === "ticket") {
      setBulkState({
        price: null,
        strike_out: null,
        wholesale: null,
        commission: null,
        min: null,
        total_ts: null,
        disable_promos: "0",
        is_disabled_ts: "0",
        disable_promo_change: 1,
        disable_sell_change: 1,
      });
    }
    if (type === "option") {
      const defaultOption = data?.additional_options.find(
        (i) => i.id === option_type
      );
      setBulkState({
        option_is_disabled: "0",
        disable_sell_change: 1,
        values:
          defaultOption?.values
            .filter((i) => !i.is_disabled)
            .map(({ id, is_disabled, apply_tax }) => ({
              value_id: id.toString(),
              price: null,
              is_disabled: is_disabled ? "1" : "0",
              apply_tax: apply_tax ? "1" : "0",
            })) || [],
      });
    }
    if (type === "schedule") {
      setBulkState({
        min_tickets: null,
        max_tickets: null,
        is_disabled: "0",
        disable_sell_change: 1,
      });
    }
    setBulkOpen(true);
  };

  return (
    <>
      <CalendarBulkDialog
        open={bulkOpen}
        onClose={onCloseBulkDialog}
        applyChanges={applyBatchChanges}
        updateLoading={updateLoading}
        state={bulkState}
        setState={setBulkState}
      />
      <CalendarDetailsDialog
        open={open}
        onClose={onCloseDetailsDialog}
        loading={loading}
        updateLoading={updateLoading}
        applyChanges={applyChanges}
      />
      <PageCardsLayout
        header={
          <InnerPageHeader
            iconTitle={{
              icon: <CalendarIcon fill="#fff" width={28} height={28} />,
              title: "Calendar Management",
            }}
            components={
              <Box display="flex" flexWrap="wrap">
                <Button
                  variant="contained"
                  style={{ marginRight: "8px" }}
                  disabled={!detailsBtn}
                  onClick={openBulkDialog}
                  className={classes.defaultBtn}
                >
                  Batch edit
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!detailsBtn || loading}
                  className={classes.primaryBtn}
                  onClick={fetchCalendarData}
                >
                  Detailed setup
                </Button>
              </Box>
            }
          />
        }
        content={<CalendarFilters />}
      />
    </>
  );
};

export default CalendarManagement;
